import React, { useState } from 'react'
import { EditOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { Timeline } from 'antd'
import { format, zonedTimeToUtc } from 'date-fns-tz'

import timezoneData from '@vacationtracker/shared/data/timezone'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'

import { ILeaveRequestDetails } from '@vacationtracker/shared/types/leave-request'
import { EventTypeEnum } from '@vacationtracker/shared/types/core-event'
import IntlMessages from '../../util/IntlMessages'
import LeaveFormatDate from '../leaves-format-date'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

const LeaveRequestDetails = ({
  leaveRequestDetails,
  locationTimezone,
  hourFormat,
  locale,
}: {
  leaveRequestDetails: Array<ILeaveRequestDetails[]>
  locationTimezone: string
  hourFormat: HourFormatEnum
  locale: LocaleEnum
}): React.ReactElement => {

  const [timezone] = useState(() => {
    const storageTimezone = localStorage.getItem('USER_TIMEZONE')
    const existTimezone = timezoneData.find((item) => item.id === (storageTimezone || locationTimezone))
    if (existTimezone) {
      return existTimezone.id
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  })


  return (
    <Timeline
      className='leave-request-details-timeline'
      mode="left"
      items={
        leaveRequestDetails[0].map((detail) => getLeaveDetailsLine({
          detail,
          timezone,
          hourFormat,
          locale,
        }))
      }
    />
  )
}

const getLeaveDetailsLine = ({
  detail,
  timezone,
  hourFormat,
  locale,
}: {
  detail: ILeaveRequestDetails
  timezone: string
  hourFormat: HourFormatEnum
  locale: LocaleEnum
}) => {
  const dateInTimezone = detail.creatorName !== 'system' ?
    zonedTimeToUtc(new Date(detail.timestamp), timezone) :
    new Date(new Date(detail.timestamp).toISOString().slice(0, -1))



  const dateTimeFormat = `MMM d, yyyy ${hourFormat === HourFormatEnum.twentyFour ? 'HH' : 'h'}:mm zzz ${hourFormat === HourFormatEnum.twentyFour ? '' : 'a'}`
  const time = format(dateInTimezone, dateTimeFormat, { timeZone: timezone })

  let text: string | JSX.Element = ''
  let dot: string | JSX.Element = ''
  let color: string | undefined = 'blue'

  switch (detail.eventType) {
    case EventTypeEnum.TOIL_REQUEST_CREATED:
    case EventTypeEnum.LEAVE_REQUEST_CREATED: {
      const hasPartDay = detail.partDay
      const formattedDate = <LeaveFormatDate
        locale={locale}
        hourFormat={hourFormat}
        startDate={detail.startDate as string}
        endDate={detail.endDate as string}
        isPartDay={detail.isPartDay as boolean}
        partDayStartHour={hasPartDay ? hasPartDay.startHour : detail.partDayStartHour as number}
        partDayEndHour={hasPartDay ? hasPartDay.endHour : detail.partDayEndHour as number}
        partDayStartMinute={hasPartDay ? hasPartDay.startMinute : 0 as number}
        partDayEndMinute={hasPartDay ? hasPartDay.endMinute : 0 as number}
      />

      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestCreated"
        values={{
          name: detail.creatorName,
          formattedDate,
        }}
      />

      break
    }
    case EventTypeEnum.TOIL_REQUEST_ADDED:
    case EventTypeEnum.LEAVE_REQUEST_ADDED: {
      const hasPartDay = detail.partDay
      const formattedDate = <LeaveFormatDate
        locale={locale}
        hourFormat={hourFormat}
        startDate={detail.startDate as string}
        endDate={detail.endDate as string}
        isPartDay={detail.isPartDay as boolean}
        partDayStartHour={hasPartDay ? hasPartDay.startHour : detail.partDayStartHour as number}
        partDayEndHour={hasPartDay ? hasPartDay.endHour : detail.partDayEndHour as number}
        partDayStartMinute={hasPartDay ? hasPartDay.startMinute : 0 as number}
        partDayEndMinute={hasPartDay ? hasPartDay.endMinute : 0 as number}
      />

      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestAdded"
        values={{
          name: detail.creatorName,
          formattedDate,
        }}
      />
      color = 'green'

      break
    }
    case EventTypeEnum.TOIL_REQUEST_UPDATED:
    case EventTypeEnum.LEAVE_REQUEST_UPDATED: {
      const hasPartDay = detail.partDay
      const formattedDate = <LeaveFormatDate
        locale={locale}
        hourFormat={hourFormat}
        startDate={detail.startDate as string}
        endDate={detail.endDate as string}
        isPartDay={detail.isPartDay as boolean}
        partDayStartHour={hasPartDay ? hasPartDay.startHour : detail.partDayStartHour as number}
        partDayEndHour={hasPartDay ? hasPartDay.endHour : detail.partDayEndHour as number}
        partDayStartMinute={hasPartDay ? hasPartDay.startMinute : 0 as number}
        partDayEndMinute={hasPartDay ? hasPartDay.endMinute : 0 as number}
      />
      const hasOldPartDay = detail.oldPartDay
      const oldFormattedDate = <LeaveFormatDate
        locale={locale}
        hourFormat={hourFormat}
        startDate={detail.oldStartDate as string}
        endDate={detail.oldEndDate as string}
        isPartDay={detail.isPartDay as boolean}
        partDayStartHour={hasOldPartDay ? hasOldPartDay.startHour : detail.oldPartDayStartHour as number}
        partDayEndHour={hasOldPartDay ? hasOldPartDay.endHour : detail.oldPartDayEndHour as number}
        partDayStartMinute={hasOldPartDay ? hasOldPartDay.startMinute : 0 as number}
        partDayEndMinute={hasOldPartDay ? hasOldPartDay.endMinute : 0 as number}
      />

      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestUpdated"
        values={{
          name: detail.creatorName,
          formattedDate,
          oldFormattedDate,
          isReasonChanged: detail.isReasonChanged,
        }}
      />
      dot = <EditOutlined style={{ color: 'orange' }} />
      color = undefined

      break
    }
    case EventTypeEnum.TOIL_REQUEST_APPROVED:
    case EventTypeEnum.LEAVE_REQUEST_APPROVED: {
      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestApproved"
        values={{
          name: detail.creatorName,
          secondLevel: (chunk) => detail.isMultiLevelApproval ? chunk : null,
        }}
      />
      dot = <CheckCircleOutlined style={{ color: 'green' }} />
      color = undefined

      break
    }
    case EventTypeEnum.LEAVE_REQUEST_STATUS_CHANGED: {

      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestStatusChanged"
        values={{
          name: detail.creatorName,
          secondLevel: (chunk) => detail.isMultiLevelApproval ? chunk : null,
        }}
      />
      dot = <CheckCircleOutlined style={{ color: 'green' }} />
      color = undefined

      break
    }
    case EventTypeEnum.TOIL_REQUEST_CANCELLED:
    case EventTypeEnum.LEAVE_REQUEST_CANCELLED: {
      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestStatusCancelled"
        values={{
          name: detail.creatorName,
        }}
      />
      color = 'red'

      break
    }
    case EventTypeEnum.TOIL_REQUEST_DENIED:
    case EventTypeEnum.LEAVE_REQUEST_DENIED: {
      text = <IntlMessages
        id="leaveRequestDetails.leaveRequestStatusDenied"
        values={{
          name: detail.creatorName,
        }}
      />
      color = 'red'
      break
    }
    case EventTypeEnum.TOIL_REQUEST_EXPIRED:
    case EventTypeEnum.LEAVE_REQUEST_EXPIRED: {
      text = <IntlMessages id="leaveRequestDetails.leaveRequestStatusExpired" />
      dot = <ClockCircleOutlined style={{ color: 'red' }} />
      color = undefined

      break
    }

  }

  return {
    key: detail.timestamp,
    ...(color && { color }),
    ...(dot && { dot }),
    children: text,
    label: time,
  }
}


export default LeaveRequestDetails